import posthog from 'posthog-js'

import { hasAcceptedAnalytics } from '@/utils/analytics-consent'

const googleAnalyticsMeasurementId =
  process.env.GATSBY_GOOGLE_ANALYTICS_MEASUREMENT_ID || 'G-ZYGDB7JR4Z'

const posthogProjectToken = process.env.GATSBY_POSTHOG_PROJECT_TOKEN

const isProduction = process.env.NODE_ENV === 'production'
const isDevelopmentEnabled = process.env.GATSBY_POSTHOG_ENABLED === 'true'
const isPostHogDisabled = process.env.GATSBY_POSTHOG_DISABLED === 'true'

const posthogEnvironment =
  process.env.GATSBY_POSTHOG_ENVIRONMENT ||
  (isProduction ? 'production' : 'local')

const isPostHogEnabled = Boolean(
  !isPostHogDisabled &&
    posthogProjectToken &&
    (isProduction || isDevelopmentEnabled)
)

let isGoogleAnalyticsInitialized = false
let isPostHogInitialized = false

const isBrowser = () => typeof window !== 'undefined'

// Strip query strings and fragments from tracked page URLs.
const getPublicPageUrl = () =>
  `${window.location.origin}${window.location.pathname}`

const deniedGoogleConsent = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
}

const acceptedGoogleConsent = {
  ...deniedGoogleConsent,
  analytics_storage: 'granted',
}

// --------------------------------------------------
// Google Analytics: Consent required
// --------------------------------------------------

const clearGoogleAnalyticsCookies = () => {
  if (!isBrowser()) return

  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim()
    if (!name.startsWith('_ga') && name !== '_gid') return

    document.cookie = `${name}=; Max-Age=0; path=/`
    document.cookie = `${name}=; Max-Age=0; path=/; domain=${window.location.hostname}`
  })
}

const initializeGoogleAnalytics = () => {
  if (!isBrowser() || !hasAcceptedAnalytics()) return

  window[`ga-disable-${googleAnalyticsMeasurementId}`] = false

  // Restore analytics consent if the visitor previously
  // rejected analytics and subsequently accepts.
  if (isGoogleAnalyticsInitialized) {
    window.gtag?.('consent', 'update', acceptedGoogleConsent)
    return
  }

  window.dataLayer = window.dataLayer || []

  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments)
    }

  window.gtag('js', new Date())
  window.gtag('consent', 'default', deniedGoogleConsent)
  window.gtag('consent', 'update', acceptedGoogleConsent)

  window.gtag('config', googleAnalyticsMeasurementId, {
    anonymize_ip: true,
    send_page_view: false,
  })

  if (!document.getElementById('collins-google-analytics-script')) {
    const script = document.createElement('script')
    script.id = 'collins-google-analytics-script'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsMeasurementId}`
    document.head.appendChild(script)
  }

  isGoogleAnalyticsInitialized = true
}

// --------------------------------------------------
// PostHog: Cookieless, privacy-preserving analytics
// --------------------------------------------------

const initializePostHog = () => {
  if (!isBrowser() || !isPostHogEnabled || isPostHogInitialized) {
    return
  }

  posthog.init(posthogProjectToken, {
    api_host: process.env.GATSBY_POSTHOG_HOST || 'https://us.i.posthog.com',

    defaults: '2026-05-30',

    // No tracking cookies or persistent user identity.
    cookieless_mode: 'always',
    person_profiles: 'never',

    // Track only explicitly captured events.
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,

    // Prevent collection of potentially sensitive
    // exception details or session recordings.
    capture_exceptions: false,
    disable_session_recording: true,

    // Respect browser Do Not Track preferences.
    respect_dnt: true,

    // Minimize URL and referral data collection.
    get_current_url: getPublicPageUrl,
    save_campaign_params: false,
    save_referrer: false,
    property_denylist: [
      '$referrer',
      '$referring_domain',
      '$initial_referrer',
      '$initial_referring_domain',
    ],
  })

  posthog.register({
    deployment_environment: posthogEnvironment,
  })

  isPostHogInitialized = true
}

// --------------------------------------------------
// Analytics initialization
// --------------------------------------------------

export const initializeAnalytics = () => {
  if (!isBrowser()) return false

  // PostHog runs cookieless regardless of cookie consent.
  initializePostHog()

  // Google Analytics requires explicit consent.
  if (!hasAcceptedAnalytics()) return false

  initializeGoogleAnalytics()
  return true
}

// --------------------------------------------------
// Disable consent-based analytics
// --------------------------------------------------

export const disableAnalytics = () => {
  if (!isBrowser()) return

  // Disable Google Analytics.
  if (window.gtag) {
    window.gtag('consent', 'update', deniedGoogleConsent)
  }

  window[`ga-disable-${googleAnalyticsMeasurementId}`] = true
  clearGoogleAnalyticsCookies()

  // PostHog intentionally remains in cookieless mode.
  // Do not call posthog.reset() or opt_out_capturing().
}

// --------------------------------------------------
// Google Analytics custom events
// --------------------------------------------------

export const trackGoogleAnalyticsEvent = ({ category, action, label }) => {
  if (!isBrowser()) return
  if (!initializeAnalytics() || !window.gtag) return

  window.gtag('event', action, {
    event_category: category,
    event_label: label,
  })
}

// --------------------------------------------------
// Pageview tracking
// --------------------------------------------------

export const trackAnalyticsPageview = () => {
  if (!isBrowser()) return

  // Initialize both providers according to their
  // respective privacy/consent configurations.
  initializeAnalytics()

  // PostHog: Always capture cookieless pageviews,
  // subject to the SDK's Do Not Track behavior.
  if (isPostHogInitialized) {
    posthog.capture('$pageview', {
      $current_url: getPublicPageUrl(),
    })
  }

  // Google Analytics: Only capture with consent.
  if (hasAcceptedAnalytics() && window.gtag) {
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
    })
  }
}
